import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const SERVICES = [
  "Brand Creation & Pre-Press",
  "Offset Printing",
  "Digital Printing",
  "Large Format / Outdoor",
  "Premium Finishing & Binding",
  "Custom Packaging",
  "Signages / Vehicle Wrapping",
  "Corporate Stationery",
  "Other",
];

export default function QuoteForm() {
  const [state, setState] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: SERVICES[0],
    quantity: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setState({ ...state, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!state.name || !state.email || !state.message) {
      toast.error("Please fill your name, email and brief.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/quote", state);
      setSent(true);
      toast.success(
        data?.email_sent
          ? "Brief received — we'll respond within 24 hours."
          : "Brief received — saved for our team to reach out."
      );
      setState({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: SERVICES[0],
        quantity: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something didn't press right. Please try again.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-transparent border border-[#F4F4F0]/40 focus:border-[#F4F4F0] focus:ring-0 outline-none px-4 py-3 font-mono text-[13px] tracking-wide placeholder:text-[#F4F4F0]/40 text-[#F4F4F0] transition-colors";
  const label =
    "font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60 mb-2 block";

  return (
    <motion.form
      onSubmit={onSubmit}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.7 }}
      className="border border-[#F4F4F0]/30 p-6 md:p-10 bg-[#0A0A0A]"
      data-testid="quote-form"
    >
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60">
            Request Form · A-01
          </div>
          <h3 className="font-display text-3xl md:text-5xl mt-1 tracking-tight">
            Brief the press.
          </h3>
        </div>
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/60 hidden md:block">
          Reply within 24h
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={label}>Name</label>
          <input
            className={field}
            value={state.name}
            onChange={update("name")}
            placeholder="Your name"
            data-testid="quote-input-name"
            required
          />
        </div>
        <div>
          <label className={label}>Email</label>
          <input
            className={field}
            type="email"
            value={state.email}
            onChange={update("email")}
            placeholder="you@company.com"
            data-testid="quote-input-email"
            required
          />
        </div>
        <div>
          <label className={label}>Phone</label>
          <input
            className={field}
            value={state.phone}
            onChange={update("phone")}
            placeholder="+91 8806 003 999"
            data-testid="quote-input-phone"
          />
        </div>
        <div>
          <label className={label}>Company</label>
          <input
            className={field}
            value={state.company}
            onChange={update("company")}
            placeholder="Brand / Company"
            data-testid="quote-input-company"
          />
        </div>
        <div>
          <label className={label}>Service</label>
          <select
            className={`${field} appearance-none pr-10 bg-[#0A0A0A]`}
            value={state.service}
            onChange={update("service")}
            data-testid="quote-input-service"
          >
            {SERVICES.map((s) => (
              <option key={s} value={s} className="bg-[#0A0A0A]">
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={label}>Quantity / Size</label>
          <input
            className={field}
            value={state.quantity}
            onChange={update("quantity")}
            placeholder="e.g. 500 pcs · 10×15 ft"
            data-testid="quote-input-quantity"
          />
        </div>
        <div className="md:col-span-2">
          <label className={label}>Brief</label>
          <textarea
            className={`${field} min-h-[140px] resize-none`}
            value={state.message}
            onChange={update("message")}
            placeholder="Tell us about the deliverable, timeline, finishes you love…"
            data-testid="quote-input-message"
            required
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F4F0]/50 max-w-[46ch]">
          By submitting, you agree to be contacted by an Arvind Printers
          Business Development Manager to scope the job.
        </div>
        <button
          type="submit"
          disabled={loading}
          className="btn-offset on-dark disabled:opacity-60"
          data-testid="quote-submit"
        >
          {loading ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending
            </>
          ) : sent ? (
            <>Sent ✓ Send Another</>
          ) : (
            <>
              Send the Brief <ArrowUpRight className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      </div>
    </motion.form>
  );
}
